/**
 * Observability calculation utilities for exoplanet transits.
 * Uses astronomy-engine for altitude calculations and astronomyService for darkness checks.
 */
import * as Astronomy from 'astronomy-engine';
import { astronomyService } from '../../../services/astronomyService';

/**
 * Get the altitude of a target at a specific time from an observatory.
 * @param {number} ra - Right ascension in degrees
 * @param {number} dec - Declination in degrees
 * @param {number} latitude - Observatory latitude
 * @param {number} longitude - Observatory longitude
 * @param {number} altitude_m - Observatory altitude in meters
 * @param {Date} date - Time to calculate altitude for
 * @returns {number|null}
 */
const getAltitudeAtTime = (ra, dec, latitude, longitude, altitude_m, date) => {
  try {
    const observer = new Astronomy.Observer(latitude, longitude, altitude_m);
    const raHours = ra / 15;
    const horizon = Astronomy.Horizon(date, observer, raHours, dec, 'normal');
    return horizon.altitude;
  } catch (error) {
    console.error('Error calculating altitude:', error);
    return null;
  }
};

/**
 * Find when a target crosses a specific altitude (rising or setting).
 * Uses binary-like 1-minute step search up to 6 hours.
 * @param {Object} transit - Transit data with exoplanet.ra and exoplanet.dec
 * @param {Object} observatory - Observatory with location data
 * @param {Date} startTime - Time to start searching from
 * @param {number} targetAltitude - Altitude to find crossing for
 * @param {boolean} searchForward - Search forward (rising) or backward (setting)
 * @returns {Date|null}
 */
export const findAltitudeCrossing = (transit, observatory, startTime, targetAltitude, searchForward = true) => {
  if (!transit?.exoplanet?.ra || !observatory?.location) {
    return null;
  }

  const { ra, dec } = transit.exoplanet;
  const { latitude, longitude } = observatory.location;
  const altitude_m = observatory.location.altitude_meters || 0;

  const timeStep = searchForward ? 60000 : -60000;
  const maxSteps = 360; // 6 hours

  let currentTime = new Date(startTime);
  let currentAlt = getAltitudeAtTime(ra, dec, latitude, longitude, altitude_m, currentTime);

  for (let i = 0; i < maxSteps; i++) {
    currentTime = new Date(currentTime.getTime() + timeStep);
    const nextAlt = getAltitudeAtTime(ra, dec, latitude, longitude, altitude_m, currentTime);

    if (searchForward && currentAlt < targetAltitude && nextAlt >= targetAltitude) {
      return currentTime;
    } else if (!searchForward && currentAlt >= targetAltitude && nextAlt < targetAltitude) {
      return currentTime;
    }

    currentAlt = nextAlt;
  }

  return null;
};

/**
 * Calculate target altitudes at key observation time points, adjusting for altitude constraints.
 * @param {Object} transit - Transit data with exoplanet.ra/dec and transit.time_utc/duration_hours
 * @param {Object} observatory - Observatory with location data
 * @param {Date} observationStart - Planned observation start time
 * @param {Date} observationEnd - Planned observation end time
 * @param {number} [minAltitudeConstraint] - Minimum altitude constraint in degrees
 * @returns {Object|null} Altitude data at key time points
 */
export const calculateTargetAltitudes = (transit, observatory, observationStart, observationEnd, minAltitudeConstraint) => {
  if (!transit?.exoplanet?.ra || !observatory?.location) {
    return null;
  }

  const { ra, dec } = transit.exoplanet;
  const { latitude, longitude } = observatory.location;
  const altitude_m = observatory.location.altitude_meters || 0;

  const getAlt = (date) => getAltitudeAtTime(ra, dec, latitude, longitude, altitude_m, date);

  const midpointTime = new Date(transit.transit.time_utc);
  const halfDurationMs = (transit.transit.duration_hours || 0) * 3600000 / 2;
  const ingressTime = new Date(midpointTime.getTime() - halfDurationMs);
  const egressTime = new Date(midpointTime.getTime() + halfDurationMs);

  let adjustedObsStart = observationStart;
  let adjustedObsEnd = observationEnd;

  if (minAltitudeConstraint !== undefined) {
    const startAlt = getAlt(observationStart);
    const endAlt = getAlt(observationEnd);

    if (startAlt !== null && startAlt < minAltitudeConstraint) {
      const riseTime = findAltitudeCrossing(transit, observatory, observationStart, minAltitudeConstraint, true);
      if (riseTime && riseTime < ingressTime) {
        adjustedObsStart = riseTime;
      }
    }

    if (endAlt !== null && endAlt < minAltitudeConstraint) {
      const setTime = findAltitudeCrossing(transit, observatory, observationEnd, minAltitudeConstraint, false);
      if (setTime && setTime > egressTime) {
        adjustedObsEnd = setTime;
      }
    }
  }

  return {
    observationStart: {
      time: adjustedObsStart,
      altitude: getAlt(adjustedObsStart),
      adjusted: adjustedObsStart.getTime() !== observationStart.getTime()
    },
    ingress: {
      time: ingressTime,
      altitude: getAlt(ingressTime)
    },
    midpoint: {
      time: midpointTime,
      altitude: getAlt(midpointTime)
    },
    egress: {
      time: egressTime,
      altitude: getAlt(egressTime)
    },
    observationEnd: {
      time: adjustedObsEnd,
      altitude: getAlt(adjustedObsEnd),
      adjusted: adjustedObsEnd.getTime() !== observationEnd.getTime()
    }
  };
};

/**
 * Calculate whether an observatory can observe a complete transit.
 * Checks altitude, darkness at ingress/egress, and partial observability.
 * @param {Object} transit - Transit data
 * @param {Object} observatory - Observatory with location and configuration
 * @param {Object} options - { baselineBefore, baselineAfter, minAltitude }
 * @returns {{ canObserve: string, reason: string, details?: Object }}
 */
export const calculateTransitObservability = (transit, observatory, options) => {
  const { baselineBefore, baselineAfter, minAltitude } = options;

  if (!observatory?.location || !transit?.transit) {
    return { canObserve: 'unknown', reason: 'No observatory data' };
  }

  const midpointTime = new Date(transit.transit.time_utc);
  const halfDurationMs = (transit.transit.duration_hours || 0) * 3600000 / 2;
  const ingressTime = new Date(midpointTime.getTime() - halfDurationMs);
  const egressTime = new Date(midpointTime.getTime() + halfDurationMs);
  const observationStart = new Date(ingressTime.getTime() - (baselineBefore * 60000));
  const observationEnd = new Date(egressTime.getTime() + (baselineAfter * 60000));

  // Check altitude at ingress, midpoint, and egress
  const midpointAltitude = transit.transit.altitude || 0;
  const ingressAltitude = transit.transit.altitude_ingress ?? midpointAltitude;
  const egressAltitude = transit.transit.altitude_egress ?? midpointAltitude;
  const minTransitAltitude = Math.min(ingressAltitude, midpointAltitude, egressAltitude);

  if (minTransitAltitude < minAltitude) {
    return buildAltitudeFailure(ingressAltitude, midpointAltitude, egressAltitude, minAltitude);
  }

  // Get observatory twilight configuration
  const twilightType = observatory.configuration?.twilight_type || 'astronomical';
  const customAltitude = observatory.configuration?.custom_sun_altitude || -18;
  const calcOpts = { twilight_type: twilightType, custom_sun_altitude: customAltitude };
  const lat = observatory.location.latitude;
  const lon = observatory.location.longitude;
  const alt = observatory.location.altitude_meters || 0;

  const startData = astronomyService.calculateAstronomicalData(lat, lon, alt, observationStart, calcOpts);
  const endData = astronomyService.calculateAstronomicalData(lat, lon, alt, observationEnd, calcOpts);
  const ingressData = astronomyService.calculateAstronomicalData(lat, lon, alt, ingressTime, calcOpts);
  const egressData = astronomyService.calculateAstronomicalData(lat, lon, alt, egressTime, calcOpts);

  return evaluateDarknessObservability(
    startData, endData, ingressData, egressData,
    observationStart, observationEnd, ingressTime, egressTime,
    midpointAltitude
  );
};

/**
 * Build altitude failure result with details about which points are below limit.
 */
function buildAltitudeFailure(ingressAlt, midAlt, egressAlt, minAlt) {
  const belowPoints = [];
  if (ingressAlt < minAlt) belowPoints.push(`ingress ${ingressAlt.toFixed(0)}`);
  if (midAlt < minAlt) belowPoints.push(`mid ${midAlt.toFixed(0)}`);
  if (egressAlt < minAlt) belowPoints.push(`egress ${egressAlt.toFixed(0)}`);
  return {
    canObserve: 'no',
    reason: `Below minimum altitude at ${belowPoints.join(', ')} (limit ${minAlt})`
  };
}

/**
 * Evaluate observability based on darkness at key time points.
 */
function evaluateDarknessObservability(
  startData, endData, ingressData, egressData,
  observationStart, observationEnd, ingressTime, egressTime,
  midpointAltitude
) {
  const startIsDark = startData.is_dark;
  const endIsDark = endData.is_dark;
  const ingressIsDark = ingressData.is_dark;
  const egressIsDark = egressData.is_dark;
  const details = { startDark: startIsDark, endDark: endIsDark, ingressDark: ingressIsDark, egressDark: egressIsDark };

  const formatTimeUTC = (date) => date.toLocaleTimeString('en-US', {
    hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'UTC'
  });

  const minUsefulMinutes = 30;

  const timeBeforeEgressAvailable = egressIsDark
    ? (egressTime.getTime() - Math.max(
        observationStart.getTime(),
        startData.next_sun_event ? new Date(startData.next_sun_event).getTime() : observationStart.getTime()
      )) / 60000
    : 0;

  const timeAfterIngressAvailable = ingressIsDark
    ? (Math.min(
        observationEnd.getTime(),
        endData.next_sun_event ? new Date(endData.next_sun_event).getTime() : observationEnd.getTime()
      ) - ingressTime.getTime()) / 60000
    : 0;

  // Both start and egress in daylight
  if (!startIsDark && !egressIsDark) {
    return evaluateNoDarknessAtStartOrEgress(
      ingressIsDark, timeAfterIngressAvailable, minUsefulMinutes,
      startData, formatTimeUTC, ingressTime, details
    );
  }

  // Start in daylight, egress in darkness
  if (!startIsDark && egressIsDark) {
    return evaluateEgressOnlyDarkness(
      timeBeforeEgressAvailable, minUsefulMinutes,
      egressTime, formatTimeUTC, details
    );
  }

  // Start in darkness, end in daylight
  if (startIsDark && !endIsDark) {
    return evaluateIngressOnlyDarkness(
      egressIsDark, timeAfterIngressAvailable, minUsefulMinutes,
      observationStart, endData, formatTimeUTC, details
    );
  }

  // Check if transit itself has partial daylight
  if (!ingressIsDark || !egressIsDark) {
    return evaluatePartialTransitDarkness(ingressIsDark, egressIsDark, details);
  }

  // Fully dark - classify by altitude quality
  return classifyByAltitude(midpointAltitude, details);
}

function evaluateNoDarknessAtStartOrEgress(
  ingressIsDark, timeAfterIngressAvailable, minUseful,
  startData, formatTimeUTC, ingressTime, details
) {
  if (!ingressIsDark) {
    const sunAlt = startData.sun_altitude?.toFixed(1) || '?';
    return { canObserve: 'no', reason: `Transit occurs entirely during daylight (sun at ${sunAlt} at start)`, details };
  }
  if (timeAfterIngressAvailable >= minUseful) {
    return {
      canObserve: 'partial',
      reason: `Ingress only - can observe from ${formatTimeUTC(ingressTime)} UTC for ${Math.floor(timeAfterIngressAvailable)} minutes`,
      details: { ...details, observableMinutes: timeAfterIngressAvailable }
    };
  }
  return {
    canObserve: 'no',
    reason: `Insufficient darkness after ingress (only ${Math.floor(timeAfterIngressAvailable)} min available)`,
    details
  };
}

function evaluateEgressOnlyDarkness(
  timeBeforeEgressAvailable, minUseful, egressTime, formatTimeUTC, details
) {
  if (timeBeforeEgressAvailable >= minUseful) {
    const observableFromTime = new Date(egressTime.getTime() - timeBeforeEgressAvailable * 60000);
    return {
      canObserve: 'partial',
      reason: `Egress only - can observe ${Math.floor(timeBeforeEgressAvailable)} min before egress at ${formatTimeUTC(egressTime)} UTC`,
      details: {
        ...details,
        observableMinutes: timeBeforeEgressAvailable,
        observableFrom: formatTimeUTC(observableFromTime)
      }
    };
  }
  return {
    canObserve: 'no',
    reason: `Insufficient darkness before egress (only ${Math.floor(timeBeforeEgressAvailable)} min available)`,
    details
  };
}

function evaluateIngressOnlyDarkness(
  egressIsDark, timeAfterIngressAvailable, minUseful,
  observationStart, endData, formatTimeUTC, details
) {
  if (egressIsDark) {
    return {
      canObserve: 'partial',
      reason: 'Complete transit visible but baseline after egress cut short by dawn',
      details: { ...details, sunAltitudeAtEnd: endData.sun_altitude }
    };
  }
  if (timeAfterIngressAvailable >= minUseful) {
    return {
      canObserve: 'partial',
      reason: `Ingress + partial - observable from ${formatTimeUTC(observationStart)} UTC until dawn`,
      details
    };
  }
  return { canObserve: 'no', reason: 'Transit extends past dawn with insufficient observation time', details };
}

function evaluatePartialTransitDarkness(ingressIsDark, egressIsDark, details) {
  if (ingressIsDark && !egressIsDark) {
    return { canObserve: 'partial', reason: 'Ingress visible but egress occurs during daylight', details };
  }
  if (!ingressIsDark && egressIsDark) {
    return { canObserve: 'partial', reason: 'Egress visible but ingress occurs during daylight', details };
  }
  return { canObserve: 'no', reason: 'Transit occurs during twilight', details };
}

function classifyByAltitude(midpointAltitude, details) {
  if (midpointAltitude >= 60) {
    return {
      canObserve: 'complete',
      reason: `Excellent - fully dark, ${midpointAltitude.toFixed(0)} altitude`,
      details: { ...details, altitude: midpointAltitude }
    };
  }
  if (midpointAltitude >= 40) {
    return {
      canObserve: 'complete',
      reason: `Good - fully dark, ${midpointAltitude.toFixed(0)} altitude`,
      details: { ...details, altitude: midpointAltitude }
    };
  }
  return {
    canObserve: 'complete',
    reason: `Marginal - fully dark but low altitude (${midpointAltitude.toFixed(0)})`,
    details: { ...details, altitude: midpointAltitude }
  };
}
